import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import type { CollectionRef } from '../../../api/useCollection'

import { AlbumPhotosLarge } from './AlbumPhotosLarge'
import { AlbumPhotosSmall } from './AlbumPhotosSmall'

export interface AlbumPhotosHandle {
  focusPhotoById: (id: string) => boolean
}

interface AlbumPhotosProps {
  ids: readonly string[]
  showPhotos: boolean
  showOwnerAvatar?: boolean
  size?: 'small' | 'large'
  collectionRef: CollectionRef
}

export const AlbumPhotos = forwardRef<AlbumPhotosHandle, AlbumPhotosProps>(
  function AlbumPhotos(
    { ids, showPhotos, showOwnerAvatar, size = 'large', collectionRef },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null)
    const frameRef = useRef<number | null>(null)

    const cancelFocusRetry = useCallback(() => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }, [])

    const focusPhotoById = useCallback(
      (id: string) => {
        cancelFocusRetry()

        let tries = 0
        const maxTries = 10
        const escapedId = CSS.escape(id)

        const focusPhoto = () => {
          const container = containerRef.current
          if (!container) {
            return false
          }

          const photo = container.querySelector<HTMLElement>(
            `[data-photoid="${escapedId}"]`,
          )
          if (!photo) {
            return false
          }

          photo.scrollIntoView({ block: 'center', inline: 'nearest' })
          photo.focus()
          return true
        }

        // The grid can take a frame to paint again after the viewer route closes.
        const retry = () => {
          tries += 1
          if (focusPhoto() || tries >= maxTries) {
            frameRef.current = null
            return
          }

          frameRef.current = requestAnimationFrame(retry)
        }

        if (focusPhoto()) {
          return true
        }

        frameRef.current = requestAnimationFrame(retry)
        return false
      },
      [cancelFocusRetry],
    )

    useImperativeHandle(
      ref,
      () => ({
        focusPhotoById,
      }),
      [focusPhotoById],
    )

    useEffect(() => cancelFocusRetry, [cancelFocusRetry])

    if (!showPhotos) {
      return null
    }

    if (size === 'small') {
      return (
        <AlbumPhotosSmall
          ids={ids}
          showOwnerAvatar={showOwnerAvatar}
          collectionRef={collectionRef}
          containerRef={containerRef}
        />
      )
    }

    return (
      <AlbumPhotosLarge
        ids={ids}
        showOwnerAvatar={showOwnerAvatar}
        collectionRef={collectionRef}
        containerRef={containerRef}
      />
    )
  },
)

AlbumPhotos.displayName = 'AlbumPhotos'
